import { defineComponent, ref, reactive, computed, provide, watch, nextTick, toRef, unref, onMounted } from 'vue';
import Month from '@Components/ArrivalDeparturePicker/Month.vue';

import SelectionConfirmedEvent from '@Components/ArrivalDeparturePicker/Types/SelectionConfirmedEvent';

import fetchAvailability from '@Components/ArrivalDeparturePicker/Functions/fetchAvailability';
import * as dateFunctions from '@Functions/DateFunctions';
import ArrivalDepartureState from './Types/ArrivalDepartureState';
import createData from './Functions/createData';

import AvailabilityContainer from './Types/AvailabilityContainer';

import { nsCarouselWrapper, nsArrivalDeparturePickerOptions, NSCarouselEntry } from '@Types/NSCarouselWrapper';

import appTranslations from "@Components/ArrivalDeparturePicker/Translations/VueTranslations"

interface DateReactive {

    value: number | undefined;
}

export default defineComponent({

    components: { Month },

    props: {


        readOnly: {
            type: Boolean,
            required: false,
            default: false
        },

        arrivalDate: {
            type: String,
            required: false
        },
        departureDate: {
            type: String,
            required: false
        },

        defaultDate: {
            type: String,
            required: false
        },

        // add custom validators with regex: \d{4}-[0123]?\d-[0123]?\d
        accommodationId: {

            type: String,
            required: false
        },

        numberOfMonths: {
            type: Number,
            required: false,
            default: 18
        },


        shouldDetectBecameVisible: {
            type: Boolean,
            required: false,
            default: false
        },

        becameVisible: {
            type: Boolean,
            required: false,
        },

        hideNoPreferenceButton: {
            type: Boolean,
            required: false,
            default: false
        },

        displaySingle:{
            type: Boolean,
            required: false,
            default: false
        },

          displayConstrained: {
            type: Boolean,
            required: false,
            default: false
        }

    },

    emits: {

        selectionConfirmed: null, // no validation
        resetDate: null
    },

    async setup(props, context) {

        const root = ref<HTMLElement | null>(null);

        const propsArrivalDate = toRef(props, 'arrivalDate');
        const propsDepartureDate = toRef(props, 'departureDate');

        const arrivalDate = ref<number | undefined>(dateFunctions.parseToLtDate(propsArrivalDate.value));
        const departureDate = ref<number | undefined>(dateFunctions.parseToLtDate(propsDepartureDate.value));
        const defaultDate = ref<number | undefined>(dateFunctions.parseToLtDate(props.defaultDate));


        const availability = reactive(new AvailabilityContainer()); //define here so it can be used on watches etc.
        const validDepartures = ref<Array<number>>([]);

        let arrivalCarouselWrapper: null | NSCarouselEntry = null;

        let displayMode = "display-auto";
        if (props.displaySingle) {
            displayMode = "display-single";
        }

        if (props.displayConstrained) {
            displayMode = "display-contrained";
        }

        onMounted(() => {

            // must be called before any async calls.
            // mount our carousel js:
            arrivalCarouselWrapper = nsCarouselWrapper.create(root.value as HTMLElement, nsArrivalDeparturePickerOptions)[0];

        });

        // currently we do not watch for outside changes.
        // we can by enabling this code,
        watch(propsArrivalDate, (n, o) => {


            arrivalDate.value = dateFunctions.parseToLtDate(n);
        });

        watch(propsDepartureDate, (n, o) => {

            departureDate.value = dateFunctions.parseToLtDate(n);
        });

        const hasSelection = computed(() => { return !!arrivalDate.value && !!departureDate.value; });


        // final event to send a new arrival and departure date upwards
        const selectionConfirmed = () => {

            context.emit("selectionConfirmed", {
                arrivalDate: dateFunctions.fromLtDate(arrivalDate.value),
                departureDate: dateFunctions.fromLtDate(departureDate.value),
                arrivalDateString: arrivalDateInput.value,
                departureDateString: departureDateInput.value
            } as SelectionConfirmedEvent);

        };

        // wrapper type to convert to/from formatted string to be used in input type=date
        const arrivalDateInput = computed(
            {
                get: () => { return dateFunctions.toYYYYMMDD(dateFunctions.fromLtDate(arrivalDate.value)); },
                set: val => {
                    arrivalDate.value = dateFunctions.parseToLtDate(val.toString());
                }
            }
        );

        const hasArrivalDate = computed(() => { return !!arrivalDate.value; });

        /**
         * we always show in constrained mode to avoid confusion of buttons showing up or not.
         * otherwise only show if there is a date selected.
         */
        const showClearButton = computed(() => {

            if (props.displayConstrained) return true;
            else return hasArrivalDate.value;
        });

        // wrapper type to convert to/from formatted string to be used in input type=date
        const departureDateInput = computed(
            {
                get: () => { return dateFunctions.toYYYYMMDD(dateFunctions.fromLtDate(departureDate.value)); },
                set: val => {
                    departureDate.value = dateFunctions.parseToLtDate(val.toString());
                }
            }
        );


        const noDatePreference = () => {
            resetDate();
            selectionConfirmed();
        }



        provide('arrivalDateLt', arrivalDate);
        provide('departureDateLt', departureDate);
        provide('validDepartures', validDepartures);


        // The current state of the picker in regards to what are we picking    
        const pickState = computed(() => {

            if (props.readOnly) return ArrivalDepartureState.ReadOnly;
            // if (!arrivalDate.value) return ArrivalDepartureState.Arrival;
            if (!!arrivalDate.value && !departureDate.value) return ArrivalDepartureState.Departure;
            if (hasSelection) return ArrivalDepartureState.ResetArrival;

            // default is always arrival?
            return ArrivalDepartureState.Arrival;
        });

        const isPickStateArrival = computed(() => pickState.value === ArrivalDepartureState.Arrival || pickState.value === ArrivalDepartureState.ResetArrival);
        const isPickStateDeparture = computed(() => pickState.value === ArrivalDepartureState.Departure);

        let isFirstBecameVisible = true;

        const scrollToMonth = (arrivalMonthSelector: HTMLElement) => {

            if (root.value) {

                let pos = 0;
                const defaultScrollContainer = root.value.getElementsByClassName("adp-months-container")[0] as HTMLElement;

                pos = (arrivalMonthSelector.offsetLeft - arrivalMonthSelector.scrollLeft + arrivalMonthSelector.clientLeft);
                
                defaultScrollContainer.scrollTo({
                    top: 0,
                    left: pos,
                    behavior: 'smooth'
                }), 100;

            }
        };

        const onBecameVisible = async () => {


            // When calendar becomes visible and arrival date is picked, the calendar scrolls the selected arrival date into view.
        
            if (root.value) {

                if (arrivalDate.value !== undefined) {
                    await nextTick();

                    // Get the selected arrival date element
                    const selectedArrivalDateEl = document.querySelector(".-sa:not(.next)") as HTMLElement;

                    if (selectedArrivalDateEl) {
                        let arrivalMonthSelector = selectedArrivalDateEl.closest(".calendar") as HTMLElement;

                        scrollToMonth(arrivalMonthSelector);

                    }
                    // else :we do not scroll at all,.

                }
                else if (defaultDate.value !== undefined && isFirstBecameVisible) {


                    await nextTick();
                    // find the month that is selected.

                    const selectedArrivalDateEl = document.querySelector("[data-lt='" + defaultDate.value + "']:not(.next):not(.prev)") as HTMLElement;


                    if (selectedArrivalDateEl) {
                        let arrivalMonthSelector = selectedArrivalDateEl.closest(".calendar") as HTMLElement;

                        scrollToMonth(arrivalMonthSelector);

                    }
                }
            }

            isFirstBecameVisible = false;
        };

        if (props.shouldDetectBecameVisible) {

            // if so, we will watch became visible
            watch(() => props.becameVisible, async (newv, oldv) => {

                if (newv === true && !oldv) {

                    // call becameVisible
                    await onBecameVisible();
                }
            });
        }






        // get availability if we can

        if (props.accommodationId) {

            provide('availability', availability);
            availability.data = await fetchAvailability(+props.accommodationId);


        }


        // set / create months and days
        // defaults to current month and year
        const now = new Date();
        const end = new Date(now.valueOf());
        end.setMonth(now.getMonth() + props.numberOfMonths);
        const months = reactive(createData(now.getFullYear(), now.getMonth(), end.getFullYear(), end.getMonth(), availability.data));






        // if not read only, we will bind the handler for handling click events on the 
        // the root element
        const onDayClick = (event: Event) => {

            if (pickState.value !== ArrivalDepartureState.ReadOnly && event.target !== null && event.target instanceof HTMLElement) {

                if (event.target.classList.contains('d')) {

                    const newDate = (event.target.getAttribute('data-lt'));
                    if (newDate !== null) {

                        // handle selection click, which depends on our state...
                        if ((pickState.value === ArrivalDepartureState.Arrival || pickState.value === ArrivalDepartureState.ResetArrival) && event.target.classList.contains('-a')) { // -a means valid arrival

                            // an arrival has been selected, switch to departure
                            // or
                            // a new arrival has been selected, also reset departure, and switch to
                            // departure selection.
                            arrivalDate.value = parseInt(newDate);

                            // for both states setting this to undefined is valid..
                            departureDate.value = undefined;

                            // in this state, we should probably also update valid departures,
                            // but we can let that be processed in a watch.

                        }
                        else if (pickState.value === ArrivalDepartureState.Departure && event.target.classList.contains('-d')) {

                            // departure selected, auto confirm
                            departureDate.value = parseInt(newDate);
                            selectionConfirmed();
                        }
                    }
                }
            }
        };

        const resetDate = () => {
            // If only arrival is selected, clear the selection in this component (because data is not saved yet)
            // else the data needs to be reset in the parent component (searchbar)
            if (arrivalDate.value && !departureDate.value) {
                arrivalDate.value = undefined;
            } else {

                context.emit("resetDate", {
                    arrivalDate: dateFunctions.fromLtDate(arrivalDate.value),
                    departureDate: dateFunctions.fromLtDate(departureDate.value),
                    arrivalDateString: arrivalDateInput.value,
                    departureDateString: departureDateInput.value
                } as SelectionConfirmedEvent);
            }
        };






        // watch arrival date change to update departure dates
        watch(arrivalDate, (newValue, oldValue) => {

            if (newValue !== unref(oldValue) && newValue !== undefined) { // do we need this check, or does vue do this automatically?


                // we are in accommodation mode
                if (props.accommodationId) {
                    if (availability.data !== undefined) {


                        // using current arrival date, we should check any valid departure days..
                        // we do this here to avoid having the 'day' component do this for each date..
                        const avEntry = availability.data.entries.find((value) => value[0] === newValue);
                        if (avEntry) {
                            validDepartures.value = avEntry;

                        } else {
                            validDepartures.value = [];
                        }
                    }
                }
                //  else this is the default picker (home, search)
                else {

                    // the default is to use arrival and add days?
                    const vd = new Array<number>();
                    const maxDays = 4 * 7;
                    for (let i = 1; i <= maxDays; i++) {
                        vd.push(newValue + i);
                    }
                    validDepartures.value = vd;
                }

            }
        }, {
            immediate: true
        });


        return {

            root,
            displayMode,
            months,
            isForAccommodation: props.accommodationId !== undefined,

            arrivalDate,
            departureDate,

            // computed
            showClearButton,
            hasArrivalDate,
            hasSelection,
            arrivalDateInput,
            departureDateInput,
            pickState,
            isPickStateArrival,
            isPickStateDeparture,

            // actions
            selectionConfirmed,
            onDayClick,
            resetDate,
            noDatePreference,
            arrivalDeparturePicker_Title_ChooseArrDept: appTranslations.arrivalDeparturePicker_Title_ChooseArrDept,
            arrivalDeparturePicker_Label_ChooseDeptToContinue: appTranslations.arrivalDeparturePicker_Label_ChooseDeptToContinue,
            arrivalDeparturePicker_Btn_NoDatePreference: appTranslations.arrivalDeparturePicker_Btn_NoDatePreference,
            arrivalDeparturePicker_Btn_ResetDate: appTranslations.arrivalDeparturePicker_Btn_ResetDate
        };

    }



});

